/**
 * Browser-local cache of raw `dwk_` API keys, so the `/admin/api` surface can
 * re-display a key an admin already created instead of showing it exactly once.
 *
 * WHY THIS AND NOT THE SERVER
 * ---------------------------
 * RFC-0822 (AVDP High / CWE-312, T277312390) removed the plaintext
 * `user_api_keys.key_value` column. The raw key exists server-side only for the
 * duration of the `POST /auth/api-keys` response; the listing returns masked
 * prefixes and there is no re-read endpoint. That property is deliberate and
 * stays intact — this module caches the POST response *in the admin's own
 * browser* and nowhere else.
 *
 * SECURITY POSTURE — stated plainly, not minimised
 * ------------------------------------------------
 * Values here are readable by any script running on the console origin, and NO
 * environment ships an enforcing CSP (every env sets
 * `Content-Security-Policy-Report-Only`, never `Content-Security-Policy`). `dwk_`
 * keys never expire and are not rotated on password change. This is therefore an
 * accepted, admin-only-page tradeoff — still strictly better than pre-RFC-0822
 * (no DB cleartext, no server-side re-read path, blast radius = one browser
 * profile). Mitigations implemented here: per-`user.id` bucketing,
 * {@link dropOtherUserBuckets} — called from `AuthContext` on the sign-in
 * boundary, so it runs even for an admin who never opens `/admin/api` —
 * {@link pruneRemembered} on every list load so revoked keys self-clean, and an
 * explicit forget-all control.
 *
 * NOT in `DISPOSABLE_LOCAL_STORAGE_KEYS` (`utils/supabase.ts`) — that list is
 * consumed by the quota-exceeded prune path, where eviction would silently
 * destroy the only surviving copy of a secret.
 */

import type { JsonValue } from '../lib/gotrueTypes'

export const ADMIN_API_KEY_STORAGE_KEY = 'dw_admin_api_keys_v1'

/** `{ [userId]: { [keyId]: rawKey } }` — one bucket per account. */
type KeyBuckets = Record<string, Record<string, string>>

function readBuckets(): KeyBuckets {
  let raw: string | null
  try {
    raw = window.localStorage.getItem(ADMIN_API_KEY_STORAGE_KEY)
  } catch {
    // Storage disabled (private mode / blocked cookies) — behave as empty.
    return {}
  }
  if (!raw) return {}
  // Typed as JsonValue, not `unknown`: the payload genuinely is arbitrary JSON,
  // and the shape checks below are the real enforcement. (`unknown` is also
  // rejected by the forbidden-TypeScript-syntax CI job.)
  let parsed: JsonValue
  try {
    parsed = JSON.parse(raw)
  } catch {
    return {}
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {}

  // Re-validate the shape rather than trusting it: this is attacker-writable
  // storage, and a malformed bucket must not crash the admin page.
  const buckets: KeyBuckets = {}
  for (const [userId, bucket] of Object.entries(parsed)) {
    if (typeof bucket !== 'object' || bucket === null || Array.isArray(bucket)) continue
    const clean: Record<string, string> = {}
    for (const [keyId, value] of Object.entries(bucket)) {
      if (typeof value === 'string') clean[keyId] = value
    }
    buckets[userId] = clean
  }
  return buckets
}

/** Returns false when the write was dropped (quota / permission). */
function writeBuckets(buckets: KeyBuckets): boolean {
  try {
    const populated = Object.fromEntries(
      Object.entries(buckets).filter(([, bucket]) => Object.keys(bucket).length > 0),
    )
    if (Object.keys(populated).length === 0) {
      window.localStorage.removeItem(ADMIN_API_KEY_STORAGE_KEY)
      return true
    }
    window.localStorage.setItem(ADMIN_API_KEY_STORAGE_KEY, JSON.stringify(populated))
    return true
  } catch {
    // Quota/permission failure. Still must NOT throw — the alternative would
    // break the create flow *after* the one-time secret was already minted.
    // Report it instead so the caller can avoid promising persistence it does
    // not have.
    return false
  }
}

/**
 * Cache the raw key returned by `POST /auth/api-keys` for this account.
 * Returns false when the write was dropped, so the UI can avoid claiming the
 * key is stored when it is not.
 */
export function rememberKey(userId: string, keyId: string, rawKey: string): boolean {
  if (!userId || !keyId || !rawKey) return false
  const buckets = readBuckets()
  buckets[userId] = { ...(buckets[userId] ?? {}), [keyId]: rawKey }
  return writeBuckets(buckets)
}

/** Raw keys this browser still holds for `userId`, by key id. */
export function getRememberedKeys(userId: string): Record<string, string> {
  if (!userId) return {}
  return { ...(readBuckets()[userId] ?? {}) }
}

/**
 * Forget one key (used on revoke and on per-row "Forget").
 * Returns false when the deletion could not be persisted — the raw key is then
 * STILL in storage, and the caller must not claim otherwise.
 */
export function forgetKey(userId: string, keyId: string): boolean {
  if (!userId || !keyId) return false
  const buckets = readBuckets()
  const bucket = buckets[userId]
  if (!bucket || !(keyId in bucket)) return true
  const next = { ...bucket }
  delete next[keyId]
  buckets[userId] = next
  return writeBuckets(buckets)
}

/**
 * Forget every key this browser holds for `userId`.
 * Returns false when the deletion could not be persisted. Reporting "forgotten"
 * while usable raw keys remain in localStorage is the worst possible lie for
 * this control, so the caller must surface the failure.
 */
export function forgetAll(userId: string): boolean {
  if (!userId) return false
  const buckets = readBuckets()
  if (!(userId in buckets)) return true
  delete buckets[userId]
  return writeBuckets(buckets)
}

/**
 * Drop remembered ids that the server no longer lists, so a key revoked from
 * any device (or expired out of the listing) stops being displayed here.
 * Returns the surviving map.
 */
export function pruneRemembered(userId: string, liveKeyIds: readonly string[]): Record<string, string> {
  if (!userId) return {}
  const buckets = readBuckets()
  const bucket = buckets[userId]
  if (!bucket) return {}
  const live = new Set(liveKeyIds)
  const survivors: Record<string, string> = {}
  for (const [keyId, rawKey] of Object.entries(bucket)) {
    if (live.has(keyId)) survivors[keyId] = rawKey
  }
  if (Object.keys(survivors).length !== Object.keys(bucket).length) {
    buckets[userId] = survivors
    writeBuckets(buckets)
  }
  return survivors
}

/**
 * Drop every bucket that is not `userId`.
 *
 * Called from `AuthContext` whenever a session is established (initial restore
 * and every `onAuthStateChange`), so one admin's keys never linger in a browser
 * now signed in as someone else. Deliberately NOT called from `ApiKeysSection`:
 * that component only mounts on `/admin/api`, which would leave the previous
 * admin's raw keys in place until someone happened to visit the page.
 */
export function dropOtherUserBuckets(userId: string): void {
  if (!userId) return
  const buckets = readBuckets()
  const foreign = Object.keys(buckets).filter((id) => id !== userId)
  if (foreign.length === 0) return
  for (const id of foreign) delete buckets[id]
  writeBuckets(buckets)
}
